import React, { useState } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { ReactComponent as PhoneIcon } from '@doveconviene/sf-ui/icons/phone.svg'
import { ReactComponent as FavoriteIcon } from '../../../../images/svg/favorite.svg'

import Icon from '../../../ui/Icon'
import { CTAS_TYPES } from '../../../../helpers/const'

const StyledCta = styled.a(({
  theme: {
    fonts, fontSize, colors, spaces,
  },
  color,
  backgroundColor,
  borderColor,
  gridCardMode,
}) => {
  const textTransform = gridCardMode ? 'none' : 'uppercase'
  const border = borderColor ? `1px solid ${borderColor}` : 'none'
  const height = gridCardMode ? spaces.large : (spaces.large * 2)

  return {
    width: '100%',
    height,
    color: color || colors.mid_grey,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: backgroundColor || colors.light_grey,
    textAlign: 'center',
    borderRadius: spaces.default / 2,
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
    textDecoration: 'none',
    fontFamily: fonts.primary,
    fontSize: gridCardMode ? fontSize * 0.75 : fontSize,
    fontWeight: '700',
    cursor: 'pointer',
    lineHeight: `${fontSize}px`,
    padding: '0 10px',
    textTransform,
    border,
  }
})

const StyledIcon = styled.span(({ gridCardMode, theme: { spaces } }) => {
  const size = gridCardMode ? spaces.default : spaces.large

  return {
    flex: '0 0 auto',
    height: size,
    marginRight: '5px',
    width: size,
  }
})

const CtaIcon = ({ ctaType, gridCardMode }) => {
  const ctaIcons = {
    [CTAS_TYPES.CallCta]: <PhoneIcon viewBox="0 0 24 24" />,
    [CTAS_TYPES.FavouriteCta]: <FavoriteIcon viewBox="0 0 24 24" />,
  }

  return ctaType ? (
    <StyledIcon gridCardMode={gridCardMode}>
      <Icon>
        {ctaIcons[ctaType]}
      </Icon>
    </StyledIcon>
  ) : null
}

const ProductCta = ({
  ctaType,
  href,
  target,
  text,
  toggleText,
  color,
  backgroundColor,
  borderColor,
  hasIcon,
  hasToggleIcon,
  gridCardMode,
  onClick: onClickCallback,
}) => {
  const [clicked, setClicked] = useState(false)

  const onCtaClick = () => {
    setClicked(!clicked)
    onClickCallback(ctaType)
  }
  const isIconVisibile = (!clicked && hasIcon) || (clicked && hasToggleIcon)

  return (
    <StyledCta
      data-test={`ProductCta-${ctaType}`}
      href={href}
      rel="noopener"
      target={target}
      color={color}
      borderColor={borderColor}
      backgroundColor={backgroundColor}
      gridCardMode={gridCardMode}
      onClick={onCtaClick}
    >
      {isIconVisibile ? <CtaIcon gridCardMode={gridCardMode} ctaType={ctaType} /> : null }
      {clicked && toggleText ? toggleText : text}
    </StyledCta>
  )
}

CtaIcon.propTypes = {
  ctaType: PropTypes.string.isRequired,
  gridCardMode: PropTypes.bool.isRequired,
}

ProductCta.propTypes = {
  ctaType: PropTypes.string.isRequired,
  href: PropTypes.string.isRequired,
  target: PropTypes.string,
  text: PropTypes.string.isRequired,
  color: PropTypes.string,
  backgroundColor: PropTypes.string,
  hasIcon: PropTypes.bool,
  hasToggleIcon: PropTypes.bool,
  borderColor: PropTypes.string,
  toggleText: PropTypes.string,
  gridCardMode: PropTypes.bool,
  onClick: PropTypes.func,
}

ProductCta.defaultProps = {
  target: '_blank',
  color: null,
  backgroundColor: null,
  hasIcon: false,
  hasToggleIcon: false,
  toggleText: null,
  borderColor: null,
  gridCardMode: false,
  onClick: () => false,
}

ProductCta.displayName = 'ProductCta'
ProductCta.whyDidYouRender = true

export default React.memo(ProductCta)
